import { Mutation } from "@apollo/client/react/components";
import React from "react";
import * as palette from "@govlaunch/palette";
import Popover from "@govlaunch/popover";
import { Field, Form as FinalForm } from "react-final-form";
import {
  ISuggestProductMutationVariables,
  ISuggestProductMutation,
} from "~/lib/mutations/__generated__/SuggestProduct.generated";
import SuggestProduct from "~/lib/mutations/SuggestProduct";
import validate from "~/lib/utils/validate";
import { KeyboardEscapeListener, Button } from "@govlaunch/core";
import { Margin } from "~/components/spacings";
import { FORM_ERROR } from "final-form";
import FieldValidationError from "~/components/form/FieldValidationError";
import { prefixWithHttpIfNecessary } from "@govlaunch/web/src/utils";
import { useIntl, FormattedMessage } from "react-intl";
import * as Yup from "yup";

interface ISuggestProductPopoverDesktopProps {
  isOpen: boolean;
  onRequestClose: () => any;
  onSuggestProduct: () => any;
  children: any;
}

export default function SuggestProductPopoverDesktop({
  isOpen,
  onRequestClose,
  onSuggestProduct,
  children,
}: ISuggestProductPopoverDesktopProps) {
  const intl = useIntl();

  const productSuggestionValidation = Yup.object().shape({
    name: Yup.string().required(
      intl.formatMessage({
        defaultMessage: `Can't be blank`,
        id: "Bom3o3",
      }),
    ),
    url: Yup.string().url(
      intl.formatMessage({
        defaultMessage: "Invalid URL",
        id: "SASA4f",
      }),
    ),
  });

  const maxProductSuggestions = intl.formatMessage({
    defaultMessage: `You've reached the limit of 50 product suggestions per day`,
    id: "Hbm59/",
    description: "Error message for when a user reaches the limit of product suggestions",
  });
  const couldntAddProductSuggestion = intl.formatMessage({
    defaultMessage: "Could not add product suggestion",
    id: "+FLj1P",
  });

  return (
    <Popover
      placement="bottom"
      onClickOutside={() => {
        onRequestClose();
      }}
      isOpen={isOpen}
      boxStyle={{
        boxShadow: "0 10px 40px 0 rgba(34, 35, 40, 0.2)",
      }}
      render={() => (
        <Mutation<ISuggestProductMutation, ISuggestProductMutationVariables> mutation={SuggestProduct}>
          {(suggestProduct) => (
            <FinalForm
              onSubmit={async (data) => {
                try {
                  await suggestProduct({
                    variables: data,
                  } as any);

                  onSuggestProduct();
                } catch (e: any) {
                  if (/maximum number of product suggestions/i.test(e.message)) {
                    return {
                      [FORM_ERROR]: maxProductSuggestions,
                    };
                  }

                  return {
                    [FORM_ERROR]: couldntAddProductSuggestion,
                  };
                }
              }}
              validate={validate(productSuggestionValidation)}
            >
              {({ handleSubmit, invalid, submitError }) => (
                <form
                  onSubmit={handleSubmit}
                  autoComplete="off"
                  css={{
                    padding: "16px 24px 12px 24px",
                    display: "flex",
                    flexDirection: "column",
                    minWidth: 576,
                    novalidate: true,
                  }}
                >
                  <KeyboardEscapeListener onEscape={onRequestClose} />
                  <div
                    css={{
                      display: "flex",
                    }}
                  >
                    <Margin mb={16}>
                      <div>
                        <Margin mb={8}>
                          <p
                            css={{
                              fontSize: 24,
                              fontWeight: 700,
                              color: "#086BE5",
                            }}
                          >
                            <FormattedMessage defaultMessage="See something missing?" id="RUHglt" />
                          </p>
                        </Margin>
                        <p
                          css={{
                            fontSize: 16,
                            fontWeight: 600,
                            color: palette.darkGray,
                          }}
                        >
                          <FormattedMessage
                            defaultMessage="Help us improve our marketplace, submit your suggestion!"
                            id="4FU9ZP"
                          />
                        </p>
                      </div>
                    </Margin>
                    <span
                      onClick={onRequestClose}
                      role="button"
                      aria-label="Close"
                      css={{
                        "&:hover": {
                          color: palette.mediumGray,
                        },
                        marginLeft: "auto",
                        color: palette.sealBlue,
                        fontWeight: 500,
                        display: "flex",
                        alignItems: "flex-start",
                        cursor: "pointer",
                      }}
                    >
                      <div
                        css={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: `solid 1px #ADB6C3`,
                          borderRadius: 25,
                          height: 24,
                          width: 24,
                          background: "#ADB6C3",
                        }}
                      >
                        <span
                          css={{
                            fontSize: 30,
                            color: palette.white,
                          }}
                        >
                          &times;
                        </span>
                      </div>
                    </span>
                  </div>

                  <Field name="name">
                    {({ input, meta }) => (
                      <Margin mb={16}>
                        <input
                          {...input}
                          placeholder={intl.formatMessage({
                            defaultMessage: "Product name",
                            id: "rs6XNw",
                          })}
                          type="text"
                          css={{
                            ":focus": {
                              borderColor: palette.darkSkyBlue,
                            },

                            height: 32,
                            border: `solid 1px ${palette.lightestGray}`,
                            borderRadius: 3,
                            outline: "none",
                            width: "100%",
                            fontSize: 14,
                            minWidth: 576,
                            paddingLeft: 16,
                            paddingTop: 6,
                            paddingBottom: 6,
                          }}
                        />
                        <FieldValidationError meta={meta} css={{ marginTop: 6 }} />
                      </Margin>
                    )}
                  </Field>

                  <Field name="url">
                    {({ input, meta }) => (
                      <Margin mb={16}>
                        <input
                          {...input}
                          placeholder={intl.formatMessage({
                            defaultMessage: "Paste a link to this product (optional)",
                            id: "qeOI5p",
                          })}
                          css={{
                            ":focus": {
                              borderColor: palette.darkSkyBlue,
                            },

                            type: "url",
                            height: 32,
                            border: `solid 1px ${palette.lightestGray}`,
                            borderRadius: 3,
                            outline: "none",
                            width: "100%",
                            fontSize: 14,
                            minWidth: 576,
                            paddingLeft: 16,
                            paddingTop: 6,
                            paddingBottom: 6,
                          }}
                          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                            let url = event.target.value.trim();
                            url = prefixWithHttpIfNecessary(url, true) || "";
                            input.onChange(url);
                          }}
                        />
                        <FieldValidationError meta={meta} css={{ marginTop: 6 }} />
                      </Margin>
                    )}
                  </Field>

                  <div
                    css={{
                      display: "flex",
                    }}
                  >
                    <Button
                      type="submit"
                      theme="primary"
                      color="#086BE5"
                      textColor="#ffffff"
                      disabled={invalid}
                      css={{
                        width: "100%",
                        marginRight: 10,
                        ":disabled": {
                          opacity: 0.8,
                        },
                        fontSize: 16,
                        fontWeight: 600,
                        borderRadius: 3,
                        height: 40,
                      }}
                    >
                      <FormattedMessage defaultMessage="Suggest Product" id="ofg0+9" />
                    </Button>

                    <Button
                      theme="secondary"
                      color="#3F4A5A"
                      css={{
                        width: "100%",
                        marginLeft: 10,
                        fontSize: 16,
                        fontWeight: 600,
                        borderColor: "#ADB6C3",
                        borderRadius: 3,
                        height: 40,
                      }}
                      onClick={onRequestClose}
                    >
                      <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
                    </Button>
                  </div>

                  {submitError && (
                    <Margin mt={8}>
                      <div css={{ color: palette.red, fontSize: 14 }}>{submitError}</div>
                    </Margin>
                  )}
                </form>
              )}
            </FinalForm>
          )}
        </Mutation>
      )}
    >
      {(popoverProps: any) => children(popoverProps)}
    </Popover>
  );
}
